import { z } from 'zod';
import { toISTDateString } from './dates.js';
import { PaymentGatewaySchema, type PaymentGateway } from './subscription.js';

/** Calendar-month cycles, not per-subscriber anniversary dates — one shared sweep window
 *  every property in a region hits, rather than a scheduler that has to track a distinct
 *  renewal date per subscriber. This is also why proration is never computed: a signup on
 *  the last day of the month pays the same as one on the first, so every reprice/cancel/
 *  join case is a flat rule instead of a matrix of partial-charge edge cases. */
export const SWEEP_START_DAY = 15;

/** Signups on or before this day join the CURRENT month's cycle. Derived from
 *  SWEEP_START_DAY with a 2-day dispatch buffer.
 *  TODO: source from GET /billing/config once areas get their own sweep windows. */
export const SWEEP_JOIN_CUTOFF_DAY = SWEEP_START_DAY - 2;

/** Accessor indirection so the cutoff can move behind an API later without
 *  touching call sites. */
export function getSweepJoinCutoffDay(): number {
  return SWEEP_JOIN_CUTOFF_DAY;
}

export const BILLING_TIMEZONE = 'Asia/Kolkata';

/** Razorpay e-mandate ceiling. ₹15,000 is the RBI's Additional-Factor-Authentication (AFA)
 *  threshold for recurring e-mandates — above it, a debit needs an extra auth step the
 *  autopay flow doesn't support, so it's a hard price ceiling for India, not just a config
 *  value. Also the maximum price an existing India subscriber can be repriced to without
 *  re-authorisation. */
export const INDIA_MANDATE_MAX_AMOUNT_MINOR = 1_500_000; // ₹15,000

export const DEFAULT_PRICING_REGION = 'ROW';

/** 1st of the joining month if signup is on/before the cutoff, else 1st of next month. */
export function computeServiceStartsOn(signupDate: Date): Date {
  const istDateStr = toISTDateString(signupDate);
  const [year, month, day] = istDateStr.split('-').map(Number);
  if (day <= SWEEP_JOIN_CUTOFF_DAY) {
    return new Date(Date.UTC(year, month - 1, 1));
  }
  return new Date(Date.UTC(year, month, 1));
}

/** Billing countries this catalog prices for. 'IN' = India, 'ROW' = everywhere else
 *  (DEFAULT_PRICING_REGION above). Each is a fixed "slot" on a region — exactly one
 *  active gateway serves each at a time (see plan_prices_live_idx); which gateway serves
 *  'IN' is an admin choice (Razorpay or Stripe), not a fixed mapping. */
export const BILLING_COUNTRIES = ['IN', 'ROW'] as const;

/** GET /billing/plans row. Money is integer minor units. `plan_prices`
 *  stores no amount/currency at all (the gateway's Plan/Price object is the sole source of
 *  truth) — `liveAmountMinor`/`liveCurrency` are fetched live from the gateway on every
 *  request. Null when the live gateway call fails, in which case the UI shows a "price
 *  available at checkout" message rather than a stale or fabricated number. */
export const BillingPlanSchema = z.object({
  planCode: z.string(),
  title: z.string(),
  billingCountry: z.enum(BILLING_COUNTRIES),
  gateway: PaymentGatewaySchema,
  liveAmountMinor: z.number().nullable(),
  liveCurrency: z.string().nullable(),
});
export type BillingPlan = z.infer<typeof BillingPlanSchema>;

/** GET /billing/gateway-price response — a thin, reusable live price lookup by gateway +
 *  gateway plan/price id. Null fields (rather than a 200 with nulls) aren't used here: a
 *  resolvable id returns real values, an unresolvable one 404s (see routes/billing.ts). */
export const GatewayPriceSchema = z.object({
  amountMinor: z.number(),
  currency: z.string(),
});
export type GatewayPrice = z.infer<typeof GatewayPriceSchema>;

export const GetBillingPlansResponseSchema = z.array(BillingPlanSchema);

export const CheckoutSchema = z.object({ url: z.string(), expiresAt: z.string().nullable() });
export type Checkout = z.infer<typeof CheckoutSchema>;

export const CreateCheckoutRequestSchema = z.object({ propertyId: z.string() });
export type CreateCheckoutRequest = z.infer<typeof CreateCheckoutRequestSchema>;

/** Gateway-specific `plan_prices.gateway_plan_id` prefix — matches what each dashboard
 *  actually issues (Razorpay `plan_xxx`, Stripe `price_xxx`). Enforced below so a
 *  copy-pasted product/customer/key id can't be saved as a plan id by mistake. */
const GATEWAY_PLAN_ID_PREFIX: Record<PaymentGateway, string> = {
  razorpay: 'plan_',
  stripe: 'price_',
};

/**
 * Admin-authored region price — one per (region, billing country). Mandatory fields only
 * check "non-empty" and "matches this gateway's id prefix" for now (see region admin
 * dashboard work); more validation can layer on here later without touching the route or
 * form that consume this schema. Amount/currency are deliberately NOT part of this input,
 * and not stored anywhere — the gateway's Plan/Price object (`gatewayPlanId`) is priced and
 * displayed live (see RegionService.resolvePlanAmount, BillingService.fetchLivePrice);
 * that vendor object's own dashboard is where its price history lives.
 */
export const RegionPriceInputSchema = z
  .object({
    billingCountry: z.enum(BILLING_COUNTRIES),
    gateway: PaymentGatewaySchema,
    gatewayPlanId: z.string().min(1, 'Gateway plan id is required'),
    /** Razorpay e-mandate ceiling — required only for razorpay rows, see the IN check
     *  constraint on `plan_prices` (apps/api/src/db/schema/billing.ts). Not derivable from
     *  the gateway's price object — it's a Boundr-specific repricing safety policy, so it
     *  stays admin-authored even though amount/currency no longer are. */
    mandateMaxAmountMinor: z.number().int().positive().optional(),
  })
  .superRefine((data, ctx) => {
    const expectedPrefix = GATEWAY_PLAN_ID_PREFIX[data.gateway];
    if (!data.gatewayPlanId.startsWith(expectedPrefix)) {
      ctx.addIssue({
        code: 'custom',
        path: ['gatewayPlanId'],
        message: `${data.gateway} plan id must start with "${expectedPrefix}"`,
      });
    }
    if (data.gateway === 'razorpay' && data.mandateMaxAmountMinor === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['mandateMaxAmountMinor'],
        message: 'Mandate ceiling is required for Razorpay prices',
      });
    }
  });
export type RegionPriceInput = z.infer<typeof RegionPriceInputSchema>;

/** Read-shape for an existing `plan_prices` row, returned alongside a region in the admin
 *  regions list — not exposed on the public `GET /regions` endpoint. No amount/currency: the
 *  admin editor fetches live pricing separately (GET /billing/gateway-price). */
export const RegionPriceSchema = z.object({
  id: z.string(),
  billingCountry: z.enum(BILLING_COUNTRIES),
  gateway: PaymentGatewaySchema,
  gatewayPlanId: z.string(),
  mandateMaxAmountMinor: z.number().nullable(),
});
export type RegionPrice = z.infer<typeof RegionPriceSchema>;

/** At most one price per billing country — shared by create (every country required, see
 *  RegionPriceListSchema below) and update (a subset is valid: RegionService.syncPrices
 *  leaves a country omitted from the list untouched, so submitting just the country being
 *  repriced/re-gatewayed is the normal update shape, not a validation gap). */
export const RegionPriceListSchema = z
  .array(RegionPriceInputSchema)
  .min(1, 'At least one price is required')
  .refine((prices) => new Set(prices.map((p) => p.billingCountry)).size === prices.length, {
    message: 'Only one price per billing country is allowed',
  });

/** Create-time list: every region needs an explicit price for every billing country it's
 *  reachable from — a region created with only one country priced is
 *  unbookable from the other, silently, with no server-side signal. Update keeps the looser
 *  RegionPriceListSchema since repricing one country without resubmitting the other is the
 *  intended partial-update shape. */
export const CompleteRegionPriceListSchema = RegionPriceListSchema.refine(
  (prices) =>
    BILLING_COUNTRIES.every((country) => prices.some((p) => p.billingCountry === country)),
  { message: `A price is required for every billing country (${BILLING_COUNTRIES.join(', ')})` },
);

/**
 * Gateway-agnostic webhook event vocabulary. Each adapter maps its vendor's event names
 * onto this union (`stripe.adapter.ts`, `razorpay.adapter.ts`), and the
 * `PaymentWebhookReceived` state machine routes on it — see `PAYMENT_WEBHOOK_ROUTES` in
 * workflow-routes.ts.
 *
 * Shared rather than API-local because the CDK state machine, the API's step registry and
 * the local workflow runner all have to agree on it — the same cross-package argument as
 * `WorkflowStep` and `DUNNING_STAGES`.
 */
export type NormalizedEventType =
  | 'subscription.authenticated'
  | 'subscription.activated'
  | 'payment.succeeded'
  | 'payment.failed'
  | 'payment.refunded'
  | 'subscription.cancelled'
  | 'subscription.paused'
  | 'subscription.resumed'
  | 'unknown';
